import { Modal } from '@sgcmpteltd/uikit-next';
import cx from 'clsx';
import { useMemo } from 'react';

import { LineChart } from '../line-chart';
import type { ChartData, ChartDataMonthMap } from '../line-chart/types';
import { BACKDROP_TRANSITION_DURATION_MS_DEFAULT } from './constants';
import { PriceWithCoe } from './price-with-coe';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SubModelModal: Component = ({
  isOpen,
  backdropTransitionDurationMs = BACKDROP_TRANSITION_DURATION_MS_DEFAULT,
  onClose,
  subModelData,
  carDetailData,
  coeRecentTrendList,
}) => {
  const subModelHistory = useMemo(
    () => (subModelData?.history ? [subModelData.history] : []),
    [subModelData],
  );

  const subModelCategoryList = useMemo(() => [{
    title: subModelData?.name || '',
    description: subModelData?.transmission || '',
  }], [subModelData]);

  function mergeData(history: { price: number; date: string; }[][]):
  (string | number | null)[][] {
    const allDates = new Set(history.flat().map(item => item.date));

    const groupByDate = (data: { price: number; date: string; }[]):
    Record<string, number[]> => data.reduce<
      Record<string, number[]>>((acc, { price, date }) => ({
      ...acc,
      [date]: [...(acc[date] || []), price],
    }), {});
    const groupedData: Record<string, number[]>[] = history.map(groupByDate);

    const result: (string | number | null)[][] = [];

    allDates.forEach((date) => {
      const prices = groupedData.map(group => (
        group[date] ? group[date][0] : null));

      result.push([date, ...prices]);
    });

    result.sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    return result;
  }

  const mergedData = mergeData(subModelHistory);

  const formatDateForPriceHistory = (dateString: string): string => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);

    return `${day}-${month}-${year}`;
  };

  const getFilteredDataByMonths = (months: number): ChartData[] => {
    const today = new Date();
    const pastDate = new Date(today);

    pastDate.setMonth(today.getMonth() - months);

    return mergedData.filter((item) => {
      const itemDateStr = item[0];
      if (typeof itemDateStr !== 'string') return false;
      const itemDate = new Date(itemDateStr);
      return itemDate >= pastDate && itemDate <= today;
    }).map((item) => {
      const itemDateStr = item[0];

      const formattedDate = (typeof itemDateStr === 'string' && itemDateStr)
        ? formatDateForPriceHistory(itemDateStr)
        : null;
      return [formattedDate, ...item.slice(1)] as ChartData;
    });
  };

  const sixMonthData: ChartData[] = getFilteredDataByMonths(6);
  const twelveMonthData: ChartData[] = getFilteredDataByMonths(12);
  const twentyFourMonthData: ChartData[] = getFilteredDataByMonths(24);

  const mergedDataArray: ChartDataMonthMap = {
    6: sixMonthData,
    12: twelveMonthData,
    24: twentyFourMonthData,
  };

  return (
    <Modal
      backdropTransitionDurationMs={backdropTransitionDurationMs}
      className={cx(
        styles.containerRoot,
        styles.subModelModal,
      )}
      contentClassName={cx(
        styles.containerModalContent,
        styles.isCentralContent,
      )}
      isOpen={isOpen}
      title=""
      onClose={onClose}>
      <div className={styles.containerContent}>
        <div className={styles.textModalTitle}>
          {carDetailData.make}
          {' '}
          {carDetailData.model}
          {' '}
          {subModelData?.name}
        </div>
        <LineChart
          categoryList={subModelCategoryList}
          data={mergedDataArray}
          month={12} />
        <PriceWithCoe
          coeRecentTrendList={coeRecentTrendList}
          subModelData={subModelData} />
      </div>
    </Modal>
  );
};
